/**
 * WorkerDocumentsDialog — per-worker document management.
 *
 * Closes the worker→documents half of the loop: list a single
 * worker's uploaded files (joined to the document-type catalog for
 * human labels), open/download each via its absolute `file_url`,
 * upload a new file for a chosen type, and remove one.
 *
 * Reuses the existing backend contract (organizationApi.workerDocumentsApi):
 *   - listForWorker / upload / delete
 *   - WorkerDocumentResponse.file_url is an absolute URL (local dev or
 *     S3), so <a href> opens / downloads directly — same pattern the
 *     WorkerPortal profile uses.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { useTranslation } from 'react-i18next';

import { ApiError, organizationApi, type Schema } from '../../api';
import { ConfirmDialog } from '../primitives';
import { useDocumentViewer } from '../common/useDocumentViewer';
import { activeLocale } from '../../utils/datetime';
import { translateApiError } from '../../i18n/translateApiError';

type WorkerDocument = Schema<'WorkerDocumentResponse'>;

export interface WorkerDocumentsDialogWorker {
  id: string;
  first_name: string;
  last_name: string;
}

interface Props {
  open: boolean;
  worker: WorkerDocumentsDialogWorker | null;
  documentTypes: Array<{ id: string; name: string }>;
  onClose: () => void;
}

function fmtBytes(bytes?: number | null): string {
  if (!bytes || bytes <= 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

const WorkerDocumentsDialog: React.FC<Props> = ({
  open,
  worker,
  documentTypes,
  onClose,
}) => {
  const { t } = useTranslation();
  const [docs, setDocs] = useState<WorkerDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [uploadTypeId, setUploadTypeId] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<WorkerDocument | null>(null);
  const { openDocument, viewer } = useDocumentViewer();

  const typeName = useMemo(() => {
    const m = new Map<string, string>();
    for (const dt of documentTypes) m.set(dt.id, dt.name);
    return m;
  }, [documentTypes]);

  const load = useCallback(async () => {
    if (!worker) return;
    setLoading(true);
    try {
      setDocs(await organizationApi.workerDocumentsApi.listForWorker(worker.id));
    } catch (e) {
      setToast(
        e instanceof ApiError ? translateApiError(e, t) : t('org:workerDocsDialog.loadFailed'),
      );
    } finally {
      setLoading(false);
    }
  }, [worker, t]);

  useEffect(() => {
    if (open && worker) {
      setUploadTypeId('');
      setUploadFile(null);
      load();
    }
  }, [open, worker, load]);

  const handleUpload = async () => {
    if (!worker || !uploadTypeId || !uploadFile) return;
    setUploading(true);
    try {
      await organizationApi.workerDocumentsApi.upload(
        worker.id,
        uploadTypeId,
        uploadFile,
      );
      setToast(t('org:workerDocsDialog.uploadedToast'));
      setUploadTypeId('');
      setUploadFile(null);
      await load();
    } catch (e) {
      setToast(
        e instanceof ApiError ? translateApiError(e, t) : t('org:workerDocsDialog.uploadFailed'),
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    try {
      await organizationApi.workerDocumentsApi.delete(pendingDelete.id);
      setToast(t('org:workerDocsDialog.deletedToast'));
      setPendingDelete(null);
      await load();
    } catch (e) {
      setToast(
        e instanceof ApiError ? translateApiError(e, t) : t('org:workerDocsDialog.deleteFailed'),
      );
      throw e; // keep ConfirmDialog open for retry
    }
  };

  const workerName = worker ? `${worker.first_name} ${worker.last_name}` : '';

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>
          {t('org:workerDocsDialog.title', { name: workerName })}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('org:workerDocsDialog.subtitle')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {/* Existing documents */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress size={26} />
            </Box>
          ) : docs.length === 0 ? (
            <Box
              sx={{
                p: 3,
                textAlign: 'center',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mb: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t('org:workerDocsDialog.empty')}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1} sx={{ mb: 2 }}>
              {docs.map((d) => (
                <Box
                  key={d.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    p: 1.25,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1.25}
                    alignItems="center"
                    sx={{ minWidth: 0 }}
                  >
                    <InsertDriveFileOutlinedIcon
                      sx={{ color: 'text.secondary', fontSize: 20 }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                        {d.file_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {typeName.get(d.document_type_id) ??
                          t('org:workerDocsDialog.unknownType')}
                        {fmtBytes(d.file_size) ? ` · ${fmtBytes(d.file_size)}` : ''}
                        {d.uploaded_at
                          ? ` · ${t('org:workerDocsDialog.uploadedAt', {
                              date: new Date(d.uploaded_at).toLocaleDateString(activeLocale()),
                            })}`
                          : ''}
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => openDocument(d.file_url, d.file_name)}
                      startIcon={<OpenInNewOutlinedIcon sx={{ fontSize: 14 }} />}
                      sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.75rem' }}
                    >
                      {t('org:workerDocsDialog.open')}
                    </Button>
                    <Tooltip title={t('org:workerDocsDialog.remove')}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setPendingDelete(d)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Upload a new document */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {t('org:workerDocsDialog.uploadTitle')}
          </Typography>
          {documentTypes.length === 0 ? (
            <Alert severity="info" variant="outlined" sx={{ fontSize: '0.8rem' }}>
              {t('org:workerDocsDialog.noTypes')}
            </Alert>
          ) : (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
                <InputLabel id="wd-type-label">
                  {t('org:workerDocsDialog.typeLabel')}
                </InputLabel>
                <Select
                  labelId="wd-type-label"
                  label={t('org:workerDocsDialog.typeLabel')}
                  value={uploadTypeId}
                  onChange={(e) => setUploadTypeId(e.target.value)}
                >
                  {documentTypes.map((dt) => (
                    <MenuItem key={dt.id} value={dt.id}>
                      {dt.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                component="label"
                variant={uploadFile ? 'text' : 'outlined'}
                size="small"
                sx={{ textTransform: 'none', borderRadius: 1, flexShrink: 0 }}
              >
                {uploadFile
                  ? t('org:workerDocsDialog.changeFile')
                  : t('org:workerDocsDialog.chooseFile')}
                <input
                  type="file"
                  hidden
                  onChange={(e) => {
                    setUploadFile(e.target.files?.[0] ?? null);
                    e.target.value = '';
                  }}
                />
              </Button>
              {uploadFile && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    maxWidth: 160,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={uploadFile.name}
                >
                  {uploadFile.name}
                </Typography>
              )}
              <Button
                variant="contained"
                size="small"
                disabled={!uploadTypeId || !uploadFile || uploading}
                onClick={handleUpload}
                sx={{ textTransform: 'none', borderRadius: 1, flexShrink: 0 }}
              >
                {uploading ? (
                  <CircularProgress size={18} />
                ) : (
                  t('org:workerDocsDialog.uploadBtn')
                )}
              </Button>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ py: '12px' }}>
          <Button onClick={onClose} color="inherit">
            {t('common:actions.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image preview lightbox — shared viewer (image → modal; PDF/other →
          new tab). file_url is presigned by the backend. */}
      {viewer}

      <ConfirmDialog
        open={!!pendingDelete}
        tone="danger"
        title={t('org:workerDocsDialog.deleteTitle')}
        description={t('org:workerDocsDialog.deleteDesc')}
        subject={
          pendingDelete && (
            <Box sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
              {pendingDelete.file_name}
            </Box>
          )
        }
        confirmLabel={t('common:actions.delete')}
        confirmLoadingLabel={t('common:status.deleting')}
        onConfirm={handleDelete}
        onClose={() => setPendingDelete(null)}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="info" onClose={() => setToast(null)} sx={{ width: '100%' }}>
          {toast}
        </Alert>
      </Snackbar>
    </>
  );
};

export default WorkerDocumentsDialog;
